import { ResolverResult } from 'react-hook-form';
import { t } from 'i18next';
import set from 'lodash/set';

import { getCanonicalAndErrors, terminateDataChecksWorker } from 'services/datachecks/datachecks';
import { DatacheckError, ModalityError } from 'services/datachecks/types';
import { logError } from 'services/logging/logging';
import { setIsValidating, setIsValidationComplete, setRawErrorCount } from 'stores/webedi/webedi';
import { ProcessSpecificationDTO } from 'support/types';

import { GenericError } from '../errors/errors';
import { isBigMessage } from '../messages/messages';

// Timeout for datachecks in milliseconds (15 seconds)
export const DATACHECKS_TIMEOUT = 15000;

// Validation tracking
let currentValidationId = 0;
let activeProgressInterval: NodeJS.Timeout | undefined;

// Helper functions
const setNestedError = (
  errors: Record<string, unknown>,
  path: string,
  errorType: string,
  errorMessage: string,
): void => {
  set(errors, path, { type: errorType, message: errorMessage });
};

const processDatachecksErrors = (errors: Record<string, unknown>, datachecksErrors: Array<DatacheckError>): void => {
  datachecksErrors.forEach((error) => {
    if (error.failed_checks.length > 0 && 'flat_dotnotation' in error) {
      const errorType = error.is_hidden ? 'hidden' : 'external';
      setNestedError(
        errors,
        error.flat_dotnotation,
        errorType,
        `[TITLE=${error.failed_checks[0].error.title}][DESCRIPTION=${error.failed_checks[0].error.description}]`,
      );
    }
  });
};

const processModalityErrors = (errors: Record<string, unknown>, modalityErrors: Array<ModalityError>): void => {
  modalityErrors.forEach((error) => {
    if (error.exists === false && error.modality === 'MANDATORY' && 'flat_dotnotation' in error) {
      const fieldName = error.field.name;
      const errorTitle = error.error_title || t('webedi:errorMessages.required.title', { field: fieldName });
      const errorDescription =
        error.error_description || t('webedi:errorMessages.required.description', { field: fieldName });
      const errorType = error.is_hidden ? 'hidden' : 'required';
      setNestedError(
        errors,
        error.flat_dotnotation,
        errorType,
        `[TITLE=${errorTitle}][DESCRIPTION=${errorDescription}]`,
      );
    }
  });
};

// Creates a promise that resolves with fallback data after a timeout
const createTimeoutPromise = (data: Record<string, unknown>) => {
  let timeoutId: NodeJS.Timeout | undefined;

  const promise = new Promise<{
    originalData: Record<string, unknown>;
    errors: { datachecks: Array<DatacheckError>; modality: Array<ModalityError> };
  }>((resolve) => {
    timeoutId = setTimeout(() => {
      logError(new GenericError('WebEDI V2: Datachecks timeout'));
      // Terminate the worker to cancel any ongoing operations
      terminateDataChecksWorker();

      resolve({
        originalData: data,
        errors: { datachecks: [], modality: [] },
      });
    }, DATACHECKS_TIMEOUT);
  });

  return { promise, timeoutId };
};

/**
 * Validates data using datachecks with a timeout fallback
 * If the datachecks operation takes longer than DATACHECKS_TIMEOUT,
 * it will terminate the worker and return fallback data
 */
const validateDataOrTimeout = async (
  fieldsConfiguration: ProcessSpecificationDTO['fields'],
  data: Record<string, unknown>,
  withTimeout: boolean,
): Promise<{
  originalData: Record<string, unknown>;
  errors: { datachecks: Array<DatacheckError>; modality: Array<ModalityError> };
}> => {
  // Increment validation ID and store it for this validation
  currentValidationId += 1;
  const thisValidationId = currentValidationId;
  const startTime = performance.now();

  // Clear any existing progress interval
  if (activeProgressInterval) {
    clearInterval(activeProgressInterval);
  }

  // Log progress every 15 seconds
  activeProgressInterval = setInterval(() => {
    const elapsed = Math.round((performance.now() - startTime) / 1000);
    console.log(`[Datachecks] Validation still running... (${elapsed}s elapsed)`);
  }, 15000);

  const cleanup = () => {
    // Only clear if this is still the active validation
    if (thisValidationId === currentValidationId && activeProgressInterval) {
      clearInterval(activeProgressInterval);
      activeProgressInterval = undefined;
    }
  };

  const shouldLog = () => thisValidationId === currentValidationId;

  try {
    // Create a promise with timeout
    console.log({ fieldsConfiguration, data });
    const dataCheckPromise = getCanonicalAndErrors(fieldsConfiguration, data).then(({ canonical, errors }) => ({
      originalData: canonical,
      errors,
    }));

    if (!withTimeout) {
      const result = await dataCheckPromise;
      cleanup();
      if (shouldLog()) {
        const totalTime = Math.round((performance.now() - startTime) / 1000);
        console.log(`[Datachecks] Validation completed successfully in ${totalTime}s`);
      }
      return result;
    }

    // Setup a timeout promise that resolves after DATACHECKS_TIMEOUT
    const { promise: timeoutPromise, timeoutId } = createTimeoutPromise(data);

    try {
      // Race the datachecks against the timeout
      const result = await Promise.race([dataCheckPromise, timeoutPromise]);

      console.log('Datachecks validation result (or timeout fallback):', result);
      // Clear the timeout if datachecks completed first
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cleanup();
      if (shouldLog()) {
        const totalTime = Math.round((performance.now() - startTime) / 1000);
        console.log(`[Datachecks] Validation completed in ${totalTime}s`);
      }
      return result;
    } catch (error) {
      // Clear the timeout in case of error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      throw error;
    }
  } catch (error) {
    cleanup();
    if (shouldLog()) {
      const totalTime = Math.round((performance.now() - startTime) / 1000);
      console.error(`[Datachecks] Validation failed after ${totalTime}s`, error);
    }
    throw error;
  }
};

const validateData = async (
  fieldsConfiguration: ProcessSpecificationDTO['fields'],
  data: Record<string, unknown>,
  withTimeout = true,
): Promise<ResolverResult> => {
  setIsValidating(true);

  try {
    // Use our new function to get the result with timeout handling
    const { errors } = await validateDataOrTimeout(fieldsConfiguration, data, withTimeout);

    const merged: Record<string, unknown> = {};

    processModalityErrors(merged, errors.modality);
    processDatachecksErrors(merged, errors.datachecks);

    setIsValidating(false);
    setIsValidationComplete(true);
    setRawErrorCount(Object.keys(merged).length);

    return {
      values: Object.keys(errors).length ? {} : data,
      errors: merged,
    };
  } catch (error) {
    setIsValidating(false);
    setIsValidationComplete(true);
    setRawErrorCount(0);
    logError(error);
    return {
      values: data,
      errors: {},
    };
  }
};

type DatachecksResolverParams = {
  fieldsConfiguration: ProcessSpecificationDTO['fields'] | undefined;
  withTimeout?: boolean;
};

export const datachecksResolver = ({ fieldsConfiguration, withTimeout = true }: DatachecksResolverParams) => {
  return async (data: Record<string, unknown>): Promise<ResolverResult> => {
    if (!fieldsConfiguration) {
      return { values: data, errors: {} };
    }

    if (withTimeout && isBigMessage(data)) {
      return skipValidation(data);
    }

    try {
      const result = await validateData(fieldsConfiguration, data, withTimeout);

      return result;
    } catch (error) {
      console.log('Error in datachecksResolver', error);
      logError(error);
      return { values: data, errors: {} };
    }
  };
};

const skipValidation = (data: Record<string, unknown>) => {
  setIsValidationComplete(true);
  setRawErrorCount(0);
  return { values: data, errors: {} };
};
